import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Settings, Users, Globe, Lock, AlertCircle, Activity, Hash } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useOpenPersonalDm } from '../hooks/useOpenPersonalDm';
import { useGuild } from '../hooks/useGuild';
import { useGuildMembers } from '../hooks/useGuildMembers';
import { useGuildActivity } from '../hooks/useGuildActivity';
import { useGuildTreasury } from '../hooks/useGuildTreasury';
import { useGuildChannels } from '../hooks/useGuildChannels';
import { useGuildPermissions, canOpenGuildManagement } from '../hooks/useGuildPermissions';
import { GuildJoinButton } from '../components/GuildJoinButton';
import { GuildMemberList } from '../components/GuildMemberList';
import { GuildActivityFeed } from '../components/GuildActivityFeed';
import { GuildTreasuryCard } from '../components/GuildTreasuryCard';
import { GuildRoleBadge } from '../components/GuildRoleBadge';
import { GuildLevelBadge } from '../components/GuildLevelBadge';
import { GuildChannelSidebar } from '../components/GuildChannelSidebar';
// The guild page embeds the SAME channel view the chat arche uses, so both are
// live views onto one `chat_channels` row rather than two copies of a channel.
import { EmbeddedChannelChat } from '../../chat/components/EmbeddedChannelChat';
import { CreateGuildChannelDialog } from '../components/CreateGuildChannelDialog';
import type { GuildChannelSummary } from '@/lib/api';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from '@/auth';

export function GuildDetailPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Shared with the guild management page — see the hook for why Message here
  // means the PERSONAL DM, not the guild-scoped conversation.
  const openPersonalDm = useOpenPersonalDm();
  const { guild, loading, error, refresh, join, leave } = useGuild(guildId);
  const {
    members,
    loading: membersLoading,
    refresh: refreshMembers,
    updateRole,
    removeMember,
  } = useGuildMembers(guildId);
  const {
    activities,
    loading: activityLoading,
    hasMore,
    loadMore,
    refresh: refreshActivity,
  } = useGuildActivity(guildId);
  const {
    treasury,
    loading: treasuryLoading,
    error: treasuryError,
  } = useGuildTreasury(guild?.guildId, {
    enabled: guild?.guildType === 'earning' && !!guild?.myRole,
  });

  // Channels require guild membership, so only fetch them for members.
  const isMember = !!guild?.myRole;
  // Gate on resolved permissions, not base role: a member granted
  // `manage_channels` via a custom role can create/delete channels server-side,
  // so hiding the controls from them would make the grant unreachable.
  const { can } = useGuildPermissions(isMember ? guildId : undefined);
  const canManageChannels = can('manage_channels');
  const {
    channels,
    loading: channelsLoading,
    error: channelsError,
    refresh: refreshChannels,
    createChannel,
    deleteChannel,
  } = useGuildChannels(isMember ? guildId : undefined, { pollForUnread: true });

  const queryClient = useQueryClient();
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState<GuildChannelSummary | null>(null);
  const [isDeletingChannel, setIsDeletingChannel] = useState(false);
  // null = the read-only "#activity" view (the page's default).
  const [openChannel, setOpenChannel] = useState<GuildChannelSummary | null>(null);

  /**
   * Switching panes refetches the rail. While a channel is open the chat pane
   * marks it read, so the count the server has for it is now zero — without this
   * the badge would reappear on leaving and sit there until the next poll.
   * (Leaving before the messages loaded means nothing was marked read, and the
   * refetch correctly brings the unread count back.)
   */
  const handleSelectChannel = (channel: GuildChannelSummary | null) => {
    if (openChannel && openChannel.channelId !== channel?.channelId) refreshChannels();
    setOpenChannel(channel);
  };

  const refreshAll = async () => {
    await Promise.all([refresh(), refreshMembers(), refreshActivity()]);
  };

  const handleBan = async (userId: string, reason?: string) => {
    if (!guildId) return;
    await api.banGuildMember(guildId, userId, reason);
    // The ban list is cached under its own key (management page's Bans tab) —
    // refreshAll covers guild/members/activity but not it.
    await Promise.all([
      refreshAll(),
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.bans(guildId) }),
    ]);
  };

  /**
   * Opens the GUILD-SCOPED DM with this member and jumps to it in chat. The
   * guildId is what keeps this separate from the personal DM with the same
   * person — dropping it would reopen their personal conversation instead.
   */
  const handleMessageMember = async (userId: string) => {
    if (!guildId) return;
    try {
      const { channel } = await api.getOrCreateDirectChannel(userId, { isUserId: true, guildId });
      navigate(`/a/arche_chat/channel/${channel.channelId}`);
    } catch (err) {
      toast({
        title: 'Could not open that conversation',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteChannel = async () => {
    if (!channelToDelete) return;
    setIsDeletingChannel(true);
    try {
      await deleteChannel(channelToDelete.channelId);
      toast({ title: 'Channel deleted' });
      // Leaving the deleted channel open would strand the pane on a dead id.
      if (openChannel?.channelId === channelToDelete.channelId) setOpenChannel(null);
      setChannelToDelete(null);
    } catch (err) {
      toast({
        title: 'Could not delete channel',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingChannel(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !guild) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <Link
          to="/a/arche_guilds"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Guilds
        </Link>
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Guild not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <Link
        to="/a/arche_guilds"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Guilds
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-2xl font-semibold break-words">{guild.name}</h1>
            {guild.visibility === 'private' ? (
              <Badge variant="outline">
                <Lock className="h-3 w-3 mr-1" />
                Private
              </Badge>
            ) : (
              <Badge variant="outline">
                <Globe className="h-3 w-3 mr-1" />
                Public
              </Badge>
            )}
            {guild.guildType === 'earning' && <Badge variant="secondary">Earning</Badge>}
            <GuildLevelBadge level={guild.level} />
          </div>
          {guild.description && <p className="text-muted-foreground">{guild.description}</p>}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {guild.memberCount} members
            </span>
            {guild.ownerUsername && <span className="text-sm">Owned by {guild.ownerUsername}</span>}
            {guild.myRole && <GuildRoleBadge role={guild.myRole} />}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Same predicate as the management page's own gate — a narrower set
              here would hide the page from e.g. an invite- or ban-only mod. */}
          {canOpenGuildManagement(can) && (
            <Link to={`/a/arche_guilds/${guild.guildId}/manage`}>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Manage
              </Button>
            </Link>
          )}
          <GuildJoinButton
            myRole={guild.myRole}
            visibility={guild.visibility}
            onJoin={async () => {
              await join();
              refreshMembers();
              refreshActivity();
            }}
            onLeave={async () => {
              await leave();
              refreshMembers();
              refreshActivity();
            }}
          />
        </div>
      </div>

      {/* Treasury card for earning guilds */}
      {guild.guildType === 'earning' &&
        guild.myRole &&
        (treasuryError ? (
          <Card className="border-destructive">
            <CardContent className="py-4">
              <p className="text-sm text-destructive">{treasuryError}</p>
            </CardContent>
          </Card>
        ) : (
          <GuildTreasuryCard treasury={treasury} loading={treasuryLoading} />
        ))}

      {/* Discord/Slack-style body: channels | activity | members */}
      <div className="grid gap-4 lg:grid-cols-[190px_minmax(0,1fr)_300px]">
        {/* Left rail — channels */}
        <aside className="rounded-lg border bg-card p-2 h-fit">
          {isMember ? (
            <GuildChannelSidebar
              channels={channels}
              loading={channelsLoading}
              error={channelsError}
              canManage={canManageChannels}
              activityActive={openChannel === null}
              activeChannelId={openChannel?.channelId ?? null}
              onSelectActivity={() => handleSelectChannel(null)}
              onSelectChannel={handleSelectChannel}
              onAddChannel={() => setCreateChannelOpen(true)}
              onDeleteChannel={setChannelToDelete}
            />
          ) : (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              Join this guild to see and open its channels.
            </p>
          )}
        </aside>

        {/* Center — "#activity" by default, or the selected channel's chat */}
        <main className="rounded-lg border bg-card min-w-0 flex flex-col">
          {openChannel ? (
            <>
              <div className="flex items-center gap-2 border-b px-4 py-3">
                {openChannel.isPrivate ? (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Hash className="h-4 w-4 text-muted-foreground" />
                )}
                <h2 className="text-sm font-semibold truncate">{openChannel.name ?? 'channel'}</h2>
                <Link
                  to={`/a/arche_chat/channel/${openChannel.channelId}`}
                  className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                >
                  Open in Chat
                </Link>
              </div>
              {/* Keyed so switching channels remounts the pane rather than
                  showing the previous channel's history while the new one loads. */}
              <div className="h-[60vh] min-h-0">
                <EmbeddedChannelChat
                  key={openChannel.channelId}
                  channelId={openChannel.channelId}
                  channelLabel={openChannel.name ?? undefined}
                />
              </div>
            </>
          ) : (
            <div className="p-4">
              <div className="mb-3 flex items-center gap-2 border-b pb-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold">activity</h2>
                <span className="text-xs text-muted-foreground">Read-only · guild activity</span>
              </div>
              <GuildActivityFeed
                activities={activities}
                loading={activityLoading}
                hasMore={hasMore}
                onLoadMore={loadMore}
              />
            </div>
          )}
        </main>

        {/* Right rail — members */}
        <aside className="rounded-lg border bg-card p-4 min-w-0">
          <h3 className="mb-3 text-sm font-semibold">Members</h3>
          {membersLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <div className="overflow-x-auto">
              <GuildMemberList
                members={members}
                myRole={guild.myRole}
                can={can}
                onUpdateRole={async (userId, role) => {
                  await updateRole(userId, role);
                  refreshActivity();
                }}
                onRemove={async (userId) => {
                  await removeMember(userId);
                  await refresh();
                  refreshActivity();
                }}
                onBan={handleBan}
                currentUserId={user?.userId ?? null}
                onMessage={isMember ? handleMessageMember : undefined}
                onPersonalMessage={isMember ? openPersonalDm : undefined}
              />
            </div>
          )}
        </aside>
      </div>

      <CreateGuildChannelDialog
        open={createChannelOpen}
        onOpenChange={setCreateChannelOpen}
        onCreate={createChannel}
      />

      <AlertDialog
        open={channelToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeletingChannel) setChannelToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete #{channelToDelete?.name ?? 'channel'}?</AlertDialogTitle>
            <AlertDialogDescription>
              The channel and its message history are removed for every member. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingChannel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDeleteChannel();
              }}
              disabled={isDeletingChannel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingChannel ? 'Deleting…' : 'Delete channel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
